import React, { useEffect, useState } from "react";
import { Card, Select, Calendar, Modal, Button, Row, Col, Tag, Descriptions } from "antd";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import EmptyState from "../../components/ui/EmptyState";
import { formatTimeInTz, tzLabel } from "../../helper/timezone";

// colour + label for each day status
export const STATUS = {
  present: { color: "#14B8A6", bg: "#F0FDFA", label: "Present" },
  late: { color: "#D97706", bg: "#FFFBEB", label: "Late" },
  half_day: { color: "#D97706", bg: "#FFFBEB", label: "Half day" },
  wfh: { color: "#3B82F6", bg: "#EFF6FF", label: "Work from home" },
  leave: { color: "#8B5CF6", bg: "#F5F3FF", label: "On leave" },
  holiday: { color: "#6366F1", bg: "#EEF2FF", label: "Holiday" },
  weekend: { color: "#9CA3AF", bg: "#F3F4F6", label: "Weekend" },
  absent: { color: "#EF4444", bg: "#FEF2F2", label: "Absent" },
  not_joined: { color: "#9CA3AF", bg: "#F9FAFB", label: "Not joined yet" },
  not_tracked: { color: "#9CA3AF", bg: "#F9FAFB", label: "Not tracked" },
};

const YEARS = (() => {
  const y = moment().year();
  return Array.from({ length: 8 }, (_, i) => y - 5 + i);
})();

const fmtDays = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

// Own header with year / month selects whose lists open on <body>, directly under themselves.
export const CalendarHeader = ({ value, onChange }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
    <Select
      value={value.year()}
      style={{ width: 90 }}
      onChange={(y) => onChange(value.clone().year(y))}
      options={YEARS.map((y) => ({ value: y, label: String(y) }))}
      getPopupContainer={() => document.body}
    />
    <Select
      value={value.month()}
      style={{ width: 120 }}
      onChange={(m) => onChange(value.clone().month(m))}
      options={moment.months().map((name, i) => ({ value: i, label: name }))}
      getPopupContainer={() => document.body}
    />
    <Button onClick={() => onChange(moment())}>This month</Button>
  </div>
);

const Tile = ({ label, value, color }) => (
  <Card bodyStyle={{ padding: "12px 16px" }} style={{ borderLeft: `4px solid ${color}` }}>
    <div style={{ color: "#6B7280", fontSize: 13 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700 }}>{value}</div>
  </Card>
);

export const LeaveCard = ({ title, color, kpi, year }) => (
  <Card bodyStyle={{ padding: "14px 16px" }} style={{ borderLeft: `4px solid ${color}` }}>
    <div style={{ color: "#6B7280", fontSize: 13 }}>{title}</div>
    <div style={{ fontSize: 24, fontWeight: 700 }}>
      {fmtDays(kpi.remaining)} <span style={{ fontSize: 14, fontWeight: 400, color: "#6B7280" }}>left of {fmtDays(kpi.entitled)}</span>
    </div>
    <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
      {fmtDays(kpi.taken)} taken in {year}
      {kpi.pending ? ` · ${fmtDays(kpi.pending)} pending` : ""}
      {kpi.prorated ? " · pro-rated (joined this year)" : ""}
    </div>
  </Card>
);

// The employee's own month: summary tiles, annual / sick leave balance and a day-by-day calendar.
const MyMonthTab = () => {
  const { request } = useRequest();
  const [value, setValue] = useState(moment());
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const tz = data?.employee?.timezone;
  const time = (v) => formatTimeInTz(v, tz);

  useEffect(() => {
    request({
      url: `${apiPath.attendanceMonth}?year=${value.year()}&month=${value.month() + 1}`,
      method: "GET",
      onSuccess: (res) => setData(res?.data || null),
      onError: () => setData(null),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.year(), value.month()]);

  const dayMap = {};
  (data?.days || []).forEach((d) => { dayMap[d.date] = d; });

  const cellRender = (cellDate) => {
    if (!data || cellDate.month() !== value.month()) return null;
    const d = dayMap[cellDate.format("YYYY-MM-DD")];
    if (!d || d.status === "none") return null;
    const st = STATUS[d.status];
    if (!st) return null;
    let sub = "";
    if (d.status === "leave") sub = d.leaveType || "";
    else if (d.status === "holiday") sub = d.holidayName || "";
    else if (d.presumed) sub = "No shift logged";
    else if (["present", "late", "half_day", "wfh"].includes(d.status) && d.loginAt) sub = `${time(d.loginAt)} - ${time(d.logoutAt)}`;
    return (
      <div
        onClick={() => setDetail(d)}
        style={{ background: st.bg, borderLeft: `3px solid ${st.color}`, borderRadius: 4, padding: "2px 6px", cursor: "pointer", lineHeight: 1.25 }}
      >
        <div style={{ color: st.color, fontSize: 12, fontWeight: 600 }}>{st.label}</div>
        {sub ? <div style={{ color: "#6B7280", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div> : null}
      </div>
    );
  };

  const s = data?.summary;
  const lb = data?.leaveBalance;

  return (
    <>
      {!data ? (
        <Card><EmptyState message="Loading your month…" /></Card>
      ) : (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={8} lg={4}><Tile label="Days present" value={s?.presentDays || 0} color="#14B8A6" /></Col>
            <Col xs={12} md={8} lg={4}><Tile label="Late" value={s?.lateDays || 0} color="#D97706" /></Col>
            <Col xs={12} md={8} lg={4}><Tile label="Work from home" value={s?.wfhDays || 0} color="#3B82F6" /></Col>
            <Col xs={12} md={8} lg={4}><Tile label="On leave" value={s?.leaveDays || 0} color="#8B5CF6" /></Col>
            <Col xs={12} md={8} lg={4}><Tile label="Absent" value={s?.absentDays || 0} color="#EF4444" /></Col>
            <Col xs={12} md={8} lg={4}><Tile label="Hours worked" value={s?.totalHours ?? 0} color="#6366F1" /></Col>
          </Row>
          {lb ? (
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={24} md={12}><LeaveCard title="Annual leave" color="#8B5CF6" kpi={lb.annual} year={lb.year} /></Col>
              <Col xs={24} md={12}><LeaveCard title="Sick leave" color="#F59E0B" kpi={lb.sick} year={lb.year} /></Col>
            </Row>
          ) : null}
        </>
      )}

      <Card>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {Object.entries(STATUS).map(([k, st]) => (
            <Tag key={k} color={st.bg} style={{ color: st.color, borderColor: st.color }}>{st.label}</Tag>
          ))}
        </div>
        <Calendar
          value={value}
          onPanelChange={(v) => setValue(v)}
          onChange={(v) => { if (v.month() !== value.month() || v.year() !== value.year()) setValue(v); }}
          dateCellRender={cellRender}
          headerRender={({ value: v, onChange }) => <CalendarHeader value={v} onChange={onChange} />}
          fullscreen
        />
      </Card>

      <Modal open={!!detail} title={detail ? moment(detail.date).format("dddd, DD MMMM YYYY") : ""} onCancel={() => setDetail(null)} footer={null}>
        {detail ? (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Status">{STATUS[detail.status]?.label || detail.status}</Descriptions.Item>
            {detail.leaveType ? <Descriptions.Item label="Leave type">{detail.leaveType}</Descriptions.Item> : null}
            {detail.reason ? <Descriptions.Item label="Reason">{detail.reason}</Descriptions.Item> : null}
            {detail.holidayName ? <Descriptions.Item label="Holiday">{detail.holidayName}</Descriptions.Item> : null}
            {detail.loginAt ? <Descriptions.Item label="Shift start">{time(detail.loginAt)}</Descriptions.Item> : null}
            {detail.loginAt ? <Descriptions.Item label="Shift end">{detail.logoutAt ? time(detail.logoutAt) : "Still open"}</Descriptions.Item> : null}
            {detail.loginAt ? <Descriptions.Item label="Timezone">{tzLabel(tz)}</Descriptions.Item> : null}
            {detail.hours ? <Descriptions.Item label="Hours">{detail.hours}</Descriptions.Item> : null}
            {detail.note ? <Descriptions.Item label="Note">{detail.note}</Descriptions.Item> : null}
          </Descriptions>
        ) : null}
      </Modal>
    </>
  );
};

export default MyMonthTab;
